/**
 * Workflow Lifecycle Plane Component
 * 
 * Organizes the 15 workflow types into logical lifecycle stages
 * Provides intuitive left-to-right flow for users without business knowledge
 */

import { GET_WORKFLOW_OVERVIEW, useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Container,
    Grid,
    Group,
    Modal,
    Paper,
    SegmentedControl,
    Stack,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
    UnstyledButton
} from '@mantine/core';
import {
    IconArrowRight,
    IconBolt,
    IconChartBar,
    IconGitBranch,
    IconInfoCircle,
    IconPlayerPlay,
    IconSettings
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import ExecutionResultEnhanced from './ExecutionResultEnhanced';
import { GraphQLLifecycleDashboard } from './GraphQLDashboardWidgets';
import { WorkflowMetadata } from './index';
import InstanceEnhanced from './InstanceEnhanced';
import JobStatusEnhanced from './JobStatusEnhanced';
import TimelineItemEnhanced from './TimelineItemEnhanced';

// InstancesTableView component using Tabulator
const InstancesTableView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
    const { data, loading, error } = useQuery<Record<string, any>>(GET_WORKFLOW_OVERVIEW, {
        variables: { tenant: 'test' }
    });

    const tableRef = useRef<HTMLDivElement>(null);
    const tabulator = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    const instances = data?.listDrafts || [];

    // Initialize Tabulator
    useEffect(() => {
        if (tableRef.current && !tabulator.current && instances.length > 0) {
            const timer = setTimeout(() => {
                try {
                    if (tableRef.current) {
                        tabulator.current = new TabulatorFull(tableRef.current, {
                            data: instances,
                            layout: 'fitColumns',
                            pagination: 'local',
                            paginationSize: 20,
                            sortable: true,
                            height: '400px',
                            responsiveLayout: true,
                            placeholder: "No instances found...",
                            renderHorizontal: 'virtual',
                            renderVertical: 'virtual',
                            columns: [
                                {
                                    title: 'Instance ID',
                                    field: 'key',
                                    width: 150,
                                    frozen: true,
                                    formatter: (cell: any) => {
                                        const value = cell.getValue();
                                        return `<code>${value}</code>`;
                                    }
                                },
                                {
                                    title: 'Owner',
                                    field: 'owner',
                                    width: 120,
                                    formatter: (cell: any) => {
                                        const value = cell.getValue();
                                        return `<strong>${value}</strong>`;
                                    }
                                },
                                {
                                    title: 'Last Updated',
                                    field: 'updatedMs',
                                    width: 180,
                                    formatter: (cell: any) => {
                                        const value = cell.getValue();
                                        const date = new Date(value);
                                        return `<span>${date.toLocaleString()}</span>`;
                                    },
                                    sorter: 'number'
                                },
                                {
                                    title: 'Status',
                                    field: 'deleted',
                                    width: 100,
                                    formatter: (cell: any) => {
                                        const deleted = cell.getValue();
                                        const color = deleted ? 'red' : 'green';
                                        const status = deleted ? 'Deleted' : 'Active';
                                        return `<span style="color: var(--mantine-color-${color}-6); font-weight: bold;">${status}</span>`;
                                    },
                                    sorter: (a: boolean, b: boolean) => {
                                        return (a ? 1 : 0) - (b ? 1 : 0); // Active first, then deleted
                                    }
                                }
                            ]
                        });
                    }
                } catch (error) {
                    console.error('Failed to initialize Tabulator:', error);
                }
            }, 100);

            return () => clearTimeout(timer);
        }
        return undefined;
    }, [instances]);

    // Update table data when instances change
    useEffect(() => {
        if (tabulator.current && instances.length > 0) {
            const updateTimer = setTimeout(() => {
                try {
                    if (tabulator.current) {
                        tabulator.current.setData(instances);
                    }
                } catch (error) {
                    console.error('Failed to update Tabulator data:', error);
                }
            }, 50);

            return () => clearTimeout(updateTimer);
        }
        return undefined;
    }, [instances]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (tabulator.current) {
                try {
                    tabulator.current.destroy();
                    tabulator.current = null;
                } catch (error) {
                    console.error('Failed to destroy Tabulator:', error);
                }
            }
        };
    }, []);

    if (loading) return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Text>Loading instances...</Text>
            </Paper>
        </Container>
    );

    if (error) return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Text color="red">Error loading instances: {error.message}</Text>
            </Paper>
        </Container>
    );

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Group justify="space-between" mb="md">
                    <Title order={2}>Workflow Instances ({instances.length})</Title>
                    <Button onClick={onBack}>← Back to Dashboard</Button>
                </Group>

                <div ref={tableRef} />
            </Paper>
        </Container>
    );
};

// Lifecycle stages for organizing workflow types
const LIFECYCLE_STAGES = {
    DESIGN: {
        title: '1. Design & Define',
        description: 'Create and configure workflow templates',
        icon: <IconGitBranch size={24} />,
        color: 'blue',
        types: ['ActorDescriptor', 'WorkflowDefinition', 'TransitionCatalog', 'Policy']
    },
    CONFIGURE: {
        title: '2. Configure & Schedule',
        description: 'Set up runtime configurations and triggers',
        icon: <IconSettings size={24} />,
        color: 'teal',
        types: ['Context', 'Schedule', 'Resource', 'Integration']
    },
    EXECUTE: {
        title: '3. Execute & Monitor',
        description: 'Run and track workflow instances',
        icon: <IconPlayerPlay size={24} />,
        color: 'green',
        types: ['Instance', 'Signal', 'Task', 'JobStatus']
    },
    ANALYZE: {
        title: '4. Analyze & Optimize',
        description: 'Review results and handle exceptions',
        icon: <IconChartBar size={24} />,
        color: 'orange',
        types: ['ExecutionResult', 'TimelineItem', 'Escalation', 'OptimizationRecommendation']
    }
};

// Category groupings for filtering
const CATEGORIES = {
    CORE_ENGINE: {
        label: 'Core Engine',
        description: 'Fundamental workflow components',
        types: ['WorkflowDefinition', 'Instance', 'Signal', 'ExecutionResult', 'TimelineItem', 'Task', 'JobStatus', 'ActorDescriptor', 'TransitionCatalog', 'OptimizationRecommendation']
    },
    GOVERNANCE: {
        label: 'Governance & Ops',
        description: 'Control and operational components',
        types: ['Policy', 'Context', 'Schedule', 'Resource', 'Integration', 'Escalation']
    },
    FREQUENTLY_USED: {
        label: 'Most Common',
        description: 'Components used in 80% of workflows',
        types: ['WorkflowDefinition', 'Instance', 'Task', 'Schedule', 'Policy']
    }
};

interface WorkflowLifecyclePlaneProps {
    onTypeSelect?: (type: string) => void;
    viewMode?: 'dashboard' | 'lifecycle' | 'category' | 'grid';
    defaultView?: 'dashboard' | 'lifecycle' | 'category' | 'grid';
}

export const WorkflowLifecyclePlane: React.FC<WorkflowLifecyclePlaneProps> = ({
    onTypeSelect,
    viewMode: initialViewMode,
    defaultView = 'dashboard'
}) => {
    const [viewMode, setViewMode] = useState(initialViewMode || defaultView);
    const [selectedStage, setSelectedStage] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [filterCategory, setFilterCategory] = useState<string | null>(null);
    const [showHelp, setShowHelp] = useState(false);
    const [detailView, setDetailView] = useState<string | null>(null);

    const handleTypeClick = (type: string) => {
        setSelectedType(type);
        setDetailView(type);
        onTypeSelect?.(type);
    };

    const handleNavigateToComponent = (componentType: string) => {
        setDetailView(componentType);
        setSelectedType(componentType);
        onTypeSelect?.(componentType);
    };

    const handleBackToDashboard = () => {
        setDetailView(null);
        setSelectedType(null);
        setViewMode('dashboard');
    };

    const getFilteredTypes = () => {
        if (!filterCategory) return Object.keys(WorkflowMetadata);
        return CATEGORIES[filterCategory as keyof typeof CATEGORIES]?.types || [];
    };

    const renderDashboardView = () => (
        <GraphQLLifecycleDashboard onNavigate={handleNavigateToComponent} />
    );

    const renderDetailView = () => {
        const commonProps = {
            onBack: handleBackToDashboard
        };

        switch (detailView) {
            case 'Instance':
                return <InstanceEnhanced mode="monitor" {...commonProps} />;
            case 'InstancesList':
                return <InstancesTableView {...commonProps} />;
            case 'AllJobs':
                return <JobStatusEnhanced mode="monitor" showAllJobs={true} {...commonProps} />;
            case 'JobStatus':
                return <JobStatusEnhanced mode="monitor" showActiveOnly={true} {...commonProps} />;
            case 'TimelineItem':
                return <TimelineItemEnhanced />;
            case 'ExecutionResult':
                return <ExecutionResultEnhanced mode="analyze" {...commonProps} />;
            default:
                return null;
        }
    };

    const renderLifecycleView = () => (
        <Box>
            {/* Visual Flow Indicator */}
            <Box mb="xl" style={{ position: 'relative', height: 60 }}>
                <Box
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        right: 0,
                        height: 4,
                        background: 'linear-gradient(90deg, #3b82f6, #10b981, #f59e0b, #ef4444)',
                        borderRadius: 2,
                        transform: 'translateY(-50%)'
                    }}
                />
                {Object.entries(LIFECYCLE_STAGES).map((stage, index) => (
                    <Box
                        key={stage[0]}
                        style={{
                            position: 'absolute',
                            left: `${(index * 100) / (Object.keys(LIFECYCLE_STAGES).length - 1)}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)'
                        }}
                    >
                        <ThemeIcon
                            size={48}
                            radius="xl"
                            color={stage[1].color}
                            style={{
                                background: 'white',
                                border: `3px solid var(--mantine-color-${stage[1].color}-6)`,
                                cursor: 'pointer'
                            }}
                            onClick={() => setSelectedStage(stage[0])}
                        >
                            {stage[1].icon}
                        </ThemeIcon>
                    </Box>
                ))}
            </Box>

            {/* Stage Cards with Flow Indicators */}
            <Box style={{ position: 'relative' }}>
                <Grid gutter="xl">
                    {Object.entries(LIFECYCLE_STAGES).map(([stageKey, stage], index, arr) => (
                        <Grid.Col key={stageKey} span={3}>
                            <Card
                                withBorder
                                shadow={selectedStage === stageKey ? 'lg' : 'sm'}
                                style={{
                                    borderColor: selectedStage === stageKey ? `var(--mantine-color-${stage.color}-6)` : undefined,
                                    borderWidth: selectedStage === stageKey ? 2 : 1,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    position: 'relative'
                                }}
                                onClick={() => setSelectedStage(stageKey)}
                            >
                                <Group justify="space-between" mb="xs">
                                    <ThemeIcon color={stage.color} size="sm">
                                        {stage.icon}
                                    </ThemeIcon>
                                    <Badge size="sm" color={stage.color} variant="light">
                                        {stage.types.length} types
                                    </Badge>
                                </Group>

                                <Title order={5} mb="xs">{stage.title}</Title>
                                <Text size="sm" c="dimmed" mb="md">
                                    {stage.description}
                                </Text>

                                {/* Inline arrow to next stage */}
                                {index < arr.length - 1 && (
                                    <Box style={{
                                        position: 'absolute',
                                        right: -30,
                                        top: '20%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 5
                                    }}>
                                        <IconArrowRight size={24} color="var(--mantine-color-gray-4)" />
                                    </Box>
                                )}

                                <Stack gap="xs">
                                    {stage.types.map(type => {
                                        const metadata = WorkflowMetadata[type as keyof typeof WorkflowMetadata];
                                        const isFiltered = filterCategory && !getFilteredTypes().includes(type);

                                        return (
                                            <UnstyledButton
                                                key={type}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    handleTypeClick(type);
                                                }}
                                                style={{
                                                    opacity: isFiltered ? 0.4 : 1,
                                                    pointerEvents: isFiltered ? 'none' : 'auto'
                                                }}
                                            >
                                                <Paper
                                                    withBorder
                                                    p="xs"
                                                    style={{
                                                        borderColor: selectedType === type ? `var(--mantine-color-${stage.color}-6)` : undefined,
                                                        background: selectedType === type ? `var(--mantine-color-${stage.color}-0)` : undefined
                                                    }}
                                                >
                                                    <Group gap="xs">
                                                        <Text size="lg">{metadata?.icon || '📄'}</Text>
                                                        <div style={{ flex: 1 }}>
                                                            <Text size="sm" fw={500}>{metadata?.title || type}</Text>
                                                            <Text size="xs" c="dimmed" lineClamp={1}>
                                                                {metadata?.description}
                                                            </Text>
                                                        </div>
                                                    </Group>
                                                </Paper>
                                            </UnstyledButton>
                                        );
                                    })}
                                </Stack>
                            </Card>
                        </Grid.Col>
                    ))}
                </Grid>
            </Box>
        </Box>
    );

    const renderCategoryView = () => (
        <Grid>
            {Object.entries(CATEGORIES).map(([key, category]) => (
                <Grid.Col key={key} span={4}>
                    <Card withBorder>
                        <Title order={4} mb="xs">{category.label}</Title>
                        <Text size="sm" c="dimmed" mb="md">{category.description}</Text>

                        <Stack gap="xs">
                            {category.types.map(type => {
                                const metadata = WorkflowMetadata[type as keyof typeof WorkflowMetadata];
                                return (
                                    <Button
                                        key={type}
                                        variant="light"
                                        fullWidth
                                        leftSection={<Text size="lg">{metadata?.icon}</Text>}
                                        onClick={() => handleTypeClick(type)}
                                        styles={{
                                            root: { justifyContent: 'flex-start' },
                                            label: { flex: 1 }
                                        }}
                                    >
                                        <Group justify="space-between" style={{ width: '100%' }}>
                                            <Text>{metadata?.title}</Text>
                                            <Badge size="xs" variant="dot">
                                                {metadata?.features?.length || 0} features
                                            </Badge>
                                        </Group>
                                    </Button>
                                );
                            })}
                        </Stack>
                    </Card>
                </Grid.Col>
            ))}
        </Grid>
    );

    const renderGridView = () => {
        const filteredTypes = getFilteredTypes();

        return (
            <Grid>
                {Object.entries(WorkflowMetadata).map(([type, metadata]) => {
                    const isFiltered = filterCategory && !filteredTypes.includes(type);

                    return (
                        <Grid.Col key={type} span={3} style={{ opacity: isFiltered ? 0.4 : 1 }}>
                            <Card
                                withBorder
                                shadow="sm"
                                style={{
                                    cursor: isFiltered ? 'default' : 'pointer',
                                    pointerEvents: isFiltered ? 'none' : 'auto'
                                }}
                                onClick={() => handleTypeClick(type)}
                            >
                                <Group justify="space-between" mb="xs">
                                    <Text size="xl">{metadata.icon}</Text>
                                    <Badge color={metadata.color as any} size="sm">
                                        {type}
                                    </Badge>
                                </Group>
                                <Title order={6}>{metadata.title}</Title>
                                <Text size="xs" c="dimmed" lineClamp={2}>
                                    {metadata.description}
                                </Text>
                            </Card>
                        </Grid.Col>
                    );
                })}
            </Grid>
        );
    };

    return (
        <Container size="xl" style={{ minHeight: '100vh', paddingBottom: '2rem' }}>
            <Paper shadow="sm" p="lg" style={{ position: 'relative', overflow: 'visible' }}>
                {/* Header */}
                <Group justify="space-between" mb="xl">
                    <div>
                        <Title order={2}>
                            {detailView ?
                                `${WorkflowMetadata[detailView as keyof typeof WorkflowMetadata]?.title || detailView} - Detailed View` :
                                viewMode === 'dashboard' ?
                                    '🗺️ Lifecycle Overview Dashboard' :
                                    'Workflow System Overview'
                            }
                        </Title>
                        <Text c="dimmed">
                            {detailView ?
                                `Detailed view of ${detailView} with real-time data and management controls` :
                                viewMode === 'dashboard' ?
                                    'Real-time insights into workflow instances, jobs, timeline events, and system performance' :
                                    'Navigate through workflow components using the lifecycle flow'
                            }
                        </Text>
                    </div>

                    <Group>
                        {/* Category Filter */}
                        <SegmentedControl
                            value={filterCategory || 'all'}
                            onChange={(value) => setFilterCategory(value === 'all' ? null : value)}
                            data={[
                                { label: 'All Types', value: 'all' },
                                { label: 'Core', value: 'CORE_ENGINE' },
                                { label: 'Governance', value: 'GOVERNANCE' },
                                { label: 'Common', value: 'FREQUENTLY_USED' }
                            ]}
                        />

                        {/* View Mode Switcher */}
                        <SegmentedControl
                            value={viewMode}
                            onChange={(v: string) => setViewMode(v as any)}
                            data={[
                                { label: '🏠 Dashboard', value: 'dashboard' },
                                { label: '🔄 Lifecycle', value: 'lifecycle' },
                                { label: '📂 Category', value: 'category' },
                                { label: '⚡ Grid', value: 'grid' }
                            ]}
                        />

                        {/* Help Button */}
                        <Tooltip label="Understanding the workflow system">
                            <ActionIcon
                                variant="default"
                                onClick={() => setShowHelp(true)}
                            >
                                <IconInfoCircle size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>

                {/* Main Content */}
                <Box style={{ minHeight: '400px', marginBottom: '2rem' }}>
                    {detailView ? (
                        <>
                            {/* Back to Dashboard Button */}
                            <Group mb="md">
                                <Button
                                    variant="subtle"
                                    leftSection={<IconArrowRight style={{ transform: 'rotate(180deg)' }} size={16} />}
                                    onClick={handleBackToDashboard}
                                >
                                    Back to Dashboard
                                </Button>
                                <Text size="lg" fw={500}>
                                    {WorkflowMetadata[detailView as keyof typeof WorkflowMetadata]?.title || detailView}
                                </Text>
                            </Group>
                            {renderDetailView()}
                        </>
                    ) : (
                        <>
                            {viewMode === 'dashboard' && renderDashboardView()}
                            {viewMode === 'lifecycle' && renderLifecycleView()}
                            {viewMode === 'category' && renderCategoryView()}
                            {viewMode === 'grid' && renderGridView()}
                        </>
                    )}
                </Box>

                {/* Quick Start Guide */}
                <Box mt="xl" style={{ clear: 'both' }}>
                    <Paper withBorder p="md" style={{ background: '#f8f9fa', position: 'relative', zIndex: 10 }}>
                        <Group mb="xs">
                            <IconBolt size={20} color="var(--mantine-color-yellow-6)" />
                            <Text fw={500}>Quick Start Path</Text>
                        </Group>
                        <Text size="sm" c="dimmed" mb="sm">
                            For new users, follow this simplified workflow path:
                        </Text>
                        <Group gap="xs" wrap="wrap">
                            <Badge variant="filled" color="blue">1. Actor</Badge>
                            <IconArrowRight size={16} />
                            <Badge variant="filled" color="blue">2. Workflow Definition</Badge>
                            <IconArrowRight size={16} />
                            <Badge variant="filled" color="green">3. Instance</Badge>
                            <IconArrowRight size={16} />
                            <Badge variant="filled" color="green">4. Task</Badge>
                            <IconArrowRight size={16} />
                            <Badge variant="filled" color="orange">5. Results</Badge>
                        </Group>
                    </Paper>
                </Box>
            </Paper>

            {/* Help Modal */}
            <Modal
                opened={showHelp}
                onClose={() => setShowHelp(false)}
                title="Understanding Workflow Types"
                size="lg"
            >
                <Stack>
                    <div>
                        <Title order={4} mb="xs">The Lifecycle Approach</Title>
                        <Text size="sm">
                            Our workflow system organizes components into 4 logical stages that follow
                            the natural flow of creating and running workflows:
                        </Text>
                    </div>

                    {Object.entries(LIFECYCLE_STAGES).map(([key, stage]) => (
                        <Paper key={key} withBorder p="sm">
                            <Group>
                                <ThemeIcon color={stage.color} size="lg">
                                    {stage.icon}
                                </ThemeIcon>
                                <div style={{ flex: 1 }}>
                                    <Text fw={500}>{stage.title}</Text>
                                    <Text size="sm" c="dimmed">{stage.description}</Text>
                                    <Group gap={4} mt={4}>
                                        {stage.types.map(t => (
                                            <Badge key={t} size="xs" variant="dot">
                                                {WorkflowMetadata[t as keyof typeof WorkflowMetadata]?.title}
                                            </Badge>
                                        ))}
                                    </Group>
                                </div>
                            </Group>
                        </Paper>
                    ))}

                    <div>
                        <Title order={4} mb="xs">For Beginners</Title>
                        <Text size="sm">
                            Start with the <Badge size="sm">Most Common</Badge> filter to see only
                            the 5 essential components used in most workflows. As you get comfortable,
                            explore the full lifecycle view to access advanced features.
                        </Text>
                    </div>

                    <div>
                        <Title order={4} mb="xs">Not Using BPMN</Title>
                        <Text size="sm">
                            Our system uses a <strong>state machine approach</strong> rather than BPMN.
                            This means workflows are defined as states and transitions, making them
                            easier to understand and implement without specialized business process knowledge.
                        </Text>
                    </div>
                </Stack>
            </Modal>
        </Container>
    );
};

export default WorkflowLifecyclePlane;
